import { toast } from "react-toastify";
import { Button, Input, Popconfirm, Steps } from "antd";
import {
  changeTicketStatus,
  measuringStatusTitles,
  checkListStatusTitles,
  measuringTicketApprove,
  measuringTicketReject,
  assembleStatusTitles,
  removeAllTicketEvents,
} from "../../../utils";
import { useQueries } from "react-query";
import { api } from "../../../config";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../interfaces/users";
import { CalendarAppointment } from "../../Checklist/TicketModal/CalendarAppointment";
import {
  ModulePermission,
  hasMinimumPermissions,
  isSharedComponentDisabled,
  userHasPermission,
} from "../../../utils/helpers/userHelpers";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { AttachmentsAnalysis } from "../../Assembly/TicketModal/AttachmentsAnalysis";
import { Approvement } from "../../Assembly/TicketModal/Approvement";
import { useState } from "react";

interface SidePanelInterface {
  status: number;
  ticketId: number;
  type: "checklist" | "measuring" | "assemble";
  ownerId?: string;
  onClickAgendar?: () => void;

}

export const SidePanel = ({
  status,
  ticketId,
  type,
  ownerId,
  onClickAgendar
}: SidePanelInterface) => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const [sendToScheduleComment, setSendToScheduleComment] =
    useState<string>("");
  const [idAssistTec, setIDAssistTec] = useState<string | undefined>();
  const [loading, setLoading] = useState(false)
  const [{ data: ticket, refetch }, { refetch: refetchTickets }] = useQueries([
    {
      queryFn: () => api(token).get(`ticket/${type}/${ticketId}`),
      queryKey: [
        ["measuring", "assemble"].includes(type)
          ? `tickets/${type}`
          : "tickets",
      ],
      refetchOnMount: false,
    },
    {
      queryFn: () => api(token).get(`tickets/${type}/board`),
      queryKey: [`tickets/${type}/board`],
      refetchOnMount: false,
    },
  ]);
  const renderStatus = () => {
    if (type === "measuring" || type === "checklist") {
      if (status === 5) return "error";
      if (status === 4) return "finish";
      if (status === 6) return "error";
      return "process";
    } else if (type === "assemble") {
      if (status === 4 || status === 9) return "error";
      if (status === 8 || status === 6 || status === 5) return "finish";
      return "process";
    }
  };

  const getStepsTitles = (type: string) => {
    switch (type) {
      case "measuring":
        return measuringStatusTitles;
      case "checklist":
        return checkListStatusTitles;
      case "assemble":
        return assembleStatusTitles;
      default:
        return [];
    }
  };
  const stepsTitles = getStepsTitles(type);

  const handleRefetch = () => {
    refetch();
    refetchTickets();
  };

  const handleTicketStatus = async (status: number, ticketId: number) => {
    await changeTicketStatus(status, ticketId, token, type);
    handleRefetch();
  };

  const handleApprove = async () => {
    await measuringTicketApprove(token, ticketId);
    refetch();
    refetchTickets();
  };

  const handleReject = async () => {
    await measuringTicketReject(token, ticketId);
    refetch();
    refetchTickets();
  };

  const showStatusUi = (status: number, step: number) => {
    if (status === step)
      return (
        <div className="float-right mr-14">
          {type === "measuring" && status === 3 ? (
            <>
              <div className="flex gap-2">
                <Popconfirm
                  placement="topLeft"
                  title={"Rejeitar Ticket"}
                  disabled={!hasMinimumPermissions("measuring", user)}
                  description={`O ticket será rejeitado e retornará para a etapa anterior, confirma?`}
                  onConfirm={handleReject}
                  okText="Sim"
                  cancelText="Cancelar"
                >
                  <Button
                    disabled={!hasMinimumPermissions("measuring", user)}
                    danger
                    type="primary"
                    size="small"
                  >
                    <CloseCircleOutlined />
                  </Button>
                </Popconfirm>
                <Popconfirm
                  placement="topLeft"
                  title={"Aprovar relatório Ticket"}
                  disabled={!hasMinimumPermissions("measuring", user)}
                  description={`O ticket será aprovado e passará para a próxima etapa, confirma?`}
                  onConfirm={handleApprove}
                  okText="Sim"
                  cancelText="Cancelar"
                >
                  <Button
                    disabled={!hasMinimumPermissions("measuring", user)}
                    type="primary"
                    size="small"
                  >
                    <CheckCircleOutlined />
                  </Button>
                </Popconfirm>
              </div>
            </>
          ) : type === "assemble" && status === 3 ? (
            <AttachmentsAnalysis
              ticket={ticket}
              refetch={refetch}
              id={ticketId}
            />
          ) : type === "assemble" && status === 7 ? (
            <Approvement ticket={ticket} refetch={refetch} id={ticketId} />
          ) : type === "assemble" && status === 8 ? (
            <>
            {/* <Payment ticket={ticket} refetch={refetch} id={ticketId} /> */}
          </>
          ) : (
            <Popconfirm
              disabled={
                (type === "measuring" && status === 1 && !Boolean(ownerId)) ||
                (type === "measuring" &&
                  status === 2 &&
                  !userHasPermission(user, {
                    measuring: ModulePermission.ADMIN,
                  })) ||
                isSharedComponentDisabled("checklist", user)
              }
              placement="topLeft"
              title={"Atualização do Ticket"}
              description={`O ticket será enviado para próxima etapa. Confirma?`}
              onConfirm={() => handleTicketStatus(status, ticketId)}
              okText="Sim"
              cancelText="Cancelar"
            >
              <Button
                disabled={
                  (type === "measuring" && status === 1 && !Boolean(ownerId)) ||
                  (type === "measuring" &&
                    status === 2 &&
                    !userHasPermission(user, {
                      measuring: ModulePermission.ADMIN,
                    })) ||
                  type === "assemble" ||
                  isSharedComponentDisabled("checklist", user)
                }
                size="small"
              >
                Próxima Etapa
              </Button>
            </Popconfirm>
          )}
        </div>
      );

    return status > step ? "Finalizado" : "";
  };

  const renderFirstStatusDescription = () => {
    switch (type) {
      case "measuring":
        return showStatusUi(status, 1);
      case "checklist":
        return status === 1 ? (
          <CalendarAppointment
            ticket={ticket}
            id={ticketId}
            refetch={handleRefetch}
          />
        ) : (
          "Finalizado"
        );
      case "assemble":
        return status === 1 ? (
          <Button
            size="small"
            // disabled={!hasMinimumPermissions("checklist", user)}
            onClick={onClickAgendar}
          >
            Agendar
          </Button>
        ) : (
          "Finalizado"
        );
      default:
        return "";
    }
  };

  const sendToSchedule = () => {
    setLoading(true)
    api(token)
      .put(`ticket/assemble/${ticketId}`, {
        status: "ASSEMBLE_PENDING_SCHEDULE",
        ownerId: null,
        cancellationReason: null,
        cancellationImages: {
          delete: ticket.cancellationImages.map((image: any) => ({
            id: image.id,
          })),
        },
      })
      .then(async () => {
        await api(token).post(`comments`, {
          content: `Motivo do reagendamento: ${sendToScheduleComment}`,
          fieldReportId: ticket.id,
          userId: user?.id,
        });
        toast.success("Ticket enviado para agendendamento com sucesso!");
        removeAllTicketEvents(ticketId, token);
        refetch();
        refetchTickets();
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      }).finally(() => setLoading(false));
  };

    const sendToWaitingParts = async () => {
      setLoading(true);
      const products = await api(token).get(
        `/tickets/assist-products?idAssistTec=${idAssistTec}`
      );
      if (!products.length) {
        return toast.error(
          "Nenhuma informação encontrada para a Assistencia Técnica informada."
        );
      }
      api(token)
        .put(`ticket/assemble/${ticketId}`, {
          status: "ASSEMBLE_WAITING_PARTS",
          idAssistTec: Number(idAssistTec),
          products: { create: products.map((item: any) => ({
            
              product: {
                create: {
                  description: `Item Assistência - ${item.Nome}`,
                  seniority: { connect: { id: 3 } },
                  assemblyTime: 2,
                  type: 'FINAL_PRODUCT'
                },
              },
              value: 0,
              quantity: 1,
            
          }))},
        })
        .then(async () => {
          await api(token).post(`comments`, {
            content: `Motivo do reagendamento: ${sendToScheduleComment}`,
            fieldReportId: ticket.id,
            userId: user?.id,
          });
          toast.success("Ticket enviado para Aguardando Peças com sucesso!");
          removeAllTicketEvents(ticketId, token);
          refetch();
          refetchTickets();
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        })
        .finally(() => setLoading(false));
    };

  return (
    <Steps
      direction="vertical"
      className="float-left"
      style={{ height: 500 }}
      current={status - 1}
      status={renderStatus()}
      items={[
        {
          title: stepsTitles[1],
          description: renderFirstStatusDescription(),
        },
        {
          title: stepsTitles[2],
          description: showStatusUi(status, 2),
        },
        {
          title: stepsTitles[3],
          description: showStatusUi(status, 3),
        },
        {
          title: stepsTitles[4],
          description:
            status === 4 ? (
              type === "assemble" ? (
                <div className="flex flex-col">
                  {ticket.statusDescription}
                  <Popconfirm
                    zIndex={5000}
                    onConfirm={() => sendToSchedule()}
                    okButtonProps={{ loading, disabled: loading || !hasMinimumPermissions("support", user) }}
                    title={
                      <div>
                        <div>Digite o Motivo do Reagendamento:</div>
                        <Input
                          type="text"
                          onChange={(e) =>
                            setSendToScheduleComment(e.target.value)
                          }
                        />
                      </div>
                    }
                    okText="Sim"
                    cancelText="Cancelar"
                  >
                    <Button size="small" className="w-3/4 ">
                      Enviar Para Agendamento
                    </Button>
                  </Popconfirm>
                </div>
              ) : (
                "Ticket concluído."
              )
            ) : (
              ""
            ),
        },
        {
          title: stepsTitles[5],
          description: status === 5 ? (
            <div className="flex flex-col">
                  {ticket.statusDescription}
                  <Popconfirm
                    zIndex={5000}
                    okButtonProps={{ loading, disabled: loading || !hasMinimumPermissions("support", user) || !idAssistTec}}
                    onConfirm={() => sendToWaitingParts()}
                    title={
                      <div>
                        <div>Digite o ID do processo do Tático:</div>
                        <Input
                          type="text"
                          value={idAssistTec}
                          onChange={(e) =>
                            setIDAssistTec(e.target.value.replace(/\D+/g, ""))
                          }
                        />
                      </div>
                    }
                    okText="Sim"
                    cancelText="Cancelar"
                  >
                    <Button disabled={loading || !hasMinimumPermissions("support", user)} size="small" className="w-3/4 ">
                      Enviar Para Aguardando Peça
                    </Button>
                  </Popconfirm>
                </div>
          ) : "",
        },
        {
          title: stepsTitles[6],
          description: status === 6 ? "Ticket em espera no momento." : "",
        },
        {
          title: stepsTitles[7],
          description: status === 7 ? showStatusUi(status, 7) : "",
        },
        {
          title: stepsTitles[8],
        },
        {
          title: stepsTitles[9],
        },
      ].filter(
        (_, i) =>
          (type === "checklist" && i < 5) ||
          type === "measuring" ||
          type === "assemble"
      )}
    />
  );
};
